import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarIcon, Phone, MessageCircle, Loader2, CheckCircle, Video } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryCodes = [
  { code: '+590', country: 'Guadeloupe', flag: '🇬🇵' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
  { code: '+377', country: 'Monaco', flag: '🇲🇨' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿' },
  { code: '+221', country: 'Senegal', flag: '🇸🇳' },
  { code: '+225', country: 'Ivory Coast', flag: '🇨🇮' },
  { code: '+237', country: 'Cameroon', flag: '🇨🇲' },
];

export const Appointment = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null); // { date, time, meet_link } une fois confirme
  const [date, setDate] = useState(null);

  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(true);

  const [formData, setFormData] = useState({
    client_name: '',
    phone: '',
    country_code: '+590',
    client_email: '',
    preferred_time: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Quand la date change, on va chercher les vrais creneaux libres de
  // l'agenda Google (endpoint public /appointments/availability).
  useEffect(() => {
    if (!date) {
      setSlots([]);
      return;
    }
    const iso = format(date, 'yyyy-MM-dd');
    setSlotsLoading(true);
    setFormData((prev) => ({ ...prev, preferred_time: '' }));
    axios
      .get(`${API}/appointments/availability`, { params: { date: iso } })
      .then((res) => {
        setSlots(res.data.slots || []);
        setCalendarConnected(res.data.calendar_connected !== false);
      })
      .catch(() => {
        setSlots([]);
        setCalendarConnected(false);
      })
      .finally(() => setSlotsLoading(false));
  }, [date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name || !formData.phone || !date || !formData.preferred_time) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        client_name: formData.client_name,
        phone: formData.phone,
        country_code: formData.country_code,
        client_email: formData.client_email || null,
        date: format(date, 'yyyy-MM-dd'),
        time: formData.preferred_time,
        message: formData.message || null,
      };
      const res = await axios.post(`${API}/appointments/book`, payload);
      setBooking({ date: res.data.date, time: res.data.time, meet_link: res.data.meet_link });
      toast.success('Rendez-vous confirme !');
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        toast.error('Ce creneau vient d\'etre pris, choisissez-en un autre.');
        // rafraichit la liste
        const iso = format(date, 'yyyy-MM-dd');
        axios.get(`${API}/appointments/availability`, { params: { date: iso } })
          .then((r) => setSlots(r.data.slots || []));
      } else {
        toast.error('La reservation a echoue, merci de reessayer.');
      }
    }
    setLoading(false);
  };

  if (booking) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-slate-50 flex items-center justify-center" data-testid="appointment-success">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-3">Rendez-vous confirme</h1>
          <p className="text-slate-600 mb-2">
            {booking.date} a {booking.time} (heure de Guadeloupe)
          </p>
          <p className="text-slate-500 text-sm mb-8">
            Vous recevrez le rappel dans votre agenda. Le jour J, rejoignez l'appel en visio via ce lien :
          </p>
          {booking.meet_link ? (
            <a href={booking.meet_link} target="_blank" rel="noopener noreferrer">
              <Button className="bg-primary hover:bg-primary-hover text-white rounded-full gap-2">
                <Video className="w-5 h-5" />
                Rejoindre la visioconference
              </Button>
            </a>
          ) : (
            <p className="text-slate-400 text-sm">Le lien visio vous sera transmis par email.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50" data-testid="appointment-page">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Video className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">{t('bookAppointment')}</h1>
          <p className="text-slate-600">
            Choisissez un creneau reellement disponible, votre rendez-vous en visioconference est cree
            automatiquement.
          </p>
          <p className="mt-3 inline-block rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5 text-sm text-amber-800">
            Les horaires sont affiches en heure de Guadeloupe (GMT-4). Convertissez selon votre fuseau horaire.
          </p>
        </div>

        <Card className="shadow-lg border-0" data-testid="appointment-form-card">
          <CardHeader>
            <CardTitle className="text-lg">Vos informations</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="appointment-form">
              <div className="space-y-2">
                <Label htmlFor="client_name">{t('name')} *</Label>
                <Input id="client_name" name="client_name" value={formData.client_name} onChange={handleChange} placeholder="Votre nom" required data-testid="appointment-name-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_email">Email</Label>
                <Input id="client_email" name="client_email" type="email" value={formData.client_email} onChange={handleChange} placeholder="pour recevoir l'invitation et le lien visio" data-testid="appointment-email-input" />
              </div>

              <div className="space-y-2">
                <Label>{t('phone')} *</Label>
                <div className="flex gap-2">
                  <Select value={formData.country_code} onValueChange={(value) => setFormData((prev) => ({ ...prev, country_code: value }))}>
                    <SelectTrigger className="w-36" data-testid="appointment-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countryCodes.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.flag} {c.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="0690 00 00 00" className="flex-1" required data-testid="appointment-phone-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('preferredDate')} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="appointment-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, 'PPP', { locale: language === 'fr' ? fr : undefined }) : 'Selectionner une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0 || d.getDay() === 6}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>{t('preferredTime')} * <span className="font-normal text-slate-400">(heure de Guadeloupe, GMT-4)</span></Label>
                {!date ? (
                  <p className="text-sm text-slate-400">Choisissez d'abord une date.</p>
                ) : slotsLoading ? (
                  <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Recherche des creneaux libres...</p>
                ) : !calendarConnected ? (
                  <p className="text-sm text-amber-600">La prise de rendez-vous en ligne est momentanement indisponible. Contactez-nous par WhatsApp ci-dessous.</p>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-slate-500">Aucun creneau libre ce jour-la, essayez une autre date.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="appointment-slots">
                    {slots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, preferred_time: slot }))}
                        className={`py-2 rounded-lg border text-sm font-medium transition-colors ${
                          formData.preferred_time === slot
                            ? 'border-primary bg-primary text-white'
                            : 'border-slate-200 hover:border-primary'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('message')}</Label>
                <Textarea name="message" value={formData.message} onChange={handleChange} placeholder="Precisez le sujet de l'appel (optionnel)" rows={3} data-testid="appointment-message-textarea" />
              </div>

              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={loading || !calendarConnected || !formData.preferred_time}
                  className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6"
                  data-testid="appointment-submit-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Video className="w-5 h-5 mr-2" /> Confirmer le rendez-vous</>)}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                  <div className="relative flex justify-center text-xs"><span className="px-2 bg-white text-slate-500">ou</span></div>
                </div>

                <a
                  href={`https://wa.me/590690000000?text=${encodeURIComponent('Bonjour AGENTORY, je souhaite prendre rendez-vous.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button type="button" className="w-full bg-green-500 hover:bg-green-600 text-white rounded-full py-6 gap-2 shadow-lg" data-testid="whatsapp-direct-btn">
                    <MessageCircle className="w-5 h-5" />
                    {t('whatsappContact')} WhatsApp
                  </Button>
                </a>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
